import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Avatar,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import SortIcon from "@mui/icons-material/Sort";
import RefreshIcon from "@mui/icons-material/Refresh";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import { getAllCompanyNumbers } from "../actions/get-all-numbers.actions";
import { CompanyNumberItem, NumberOrderBy, OrderDirection } from "../types";
import { IContactCenterUsageByNumber } from "@components/superadmin/billing/interfaces/billingSubscriptions.interface";
import { useSuperAdminCompaniesQuery } from "@components/superadmin/views/companiesView/hook/useSuperAdminCompaniesQuery";

interface NumbersListSubcomponentProps {
  onNumbersLoaded?: (numbers: CompanyNumberItem[]) => void;
  usageItems?: IContactCenterUsageByNumber[];
  usageCurrency?: string;
  billingPeriod?: string;
}

export const NumbersListSubcomponent: React.FC<NumbersListSubcomponentProps> = ({
  onNumbersLoaded,
  usageItems = [],
  usageCurrency = "USD",
  billingPeriod,
}) => {
  const [numbers, setNumbers] = useState<CompanyNumberItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { data: companies = [] } = useSuperAdminCompaniesQuery();

  const getCompanyName = (companyId: number) => {
    const company = companies.find((company) => Number(company.id) === Number(companyId));

    return company?.name || "Unknown company";
  };

  // Sorting params
  const [order, setOrder] = useState<OrderDirection>("DESC");
  const [orderBy, setOrderBy] = useState<NumberOrderBy>("createdAt");

  // Local filtering & searching
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [providerFilter, setProviderFilter] = useState<string>("all");

  const loadNumbers = async () => {
    setLoading(true);

    try {
      const data = await getAllCompanyNumbers({
        order,
        orderBy,
      });

      console.log(
        "[NUMBERS]",
        data.map((item) => ({
          companyId: item.companyId,
          companyName: item.companyName,
          phoneNumber: item.phoneNumber,
        })),
      );

      setNumbers(data);

      if (onNumbersLoaded) {
        onNumbersLoaded(data);
      }
    } catch (err) {
      console.error("Failed to load numbers:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNumbers();
  }, [order, orderBy]);

  const toggleOrderDirection = () => {
    setOrder((prev) => (prev === "ASC" ? "DESC" : "ASC"));
  };

  const filteredNumbers = numbers.filter((item) => {
    const matchesSearch =
      !searchTerm ||
      item.phoneNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.friendlyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getCompanyName(item.companyId).toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "all" || item.status.toLowerCase() === statusFilter;
    const matchesProvider = providerFilter === "all" || item.provider.toLowerCase() === providerFilter;

    return matchesSearch && matchesStatus && matchesProvider;
  });

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    try {
      const d = new Date(dateStr);
      return new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(d);
    } catch {
      return dateStr;
    }
  };

  const getStatusChip = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return (
          <Chip
            icon={<CheckCircleOutlineIcon style={{ fontSize: 15 }} />}
            label="Active"
            size="small"
            sx={{ bgcolor: "var(--fx-colors-success-100, #D1FAE5)", color: "#15803D", fontWeight: 700, borderRadius: "6px" }}
          />
        );
      case "pending":
        return (
          <Chip
            icon={<PendingActionsIcon style={{ fontSize: 15 }} />}
            label="Pending"
            size="small"
            sx={{ bgcolor: "var(--fx-colors-yellow-100, #FFF9C2)", color: "#B45309", fontWeight: 700, borderRadius: "6px" }}
          />
        );
      case "unassigned":
      default:
        return (
          <Chip
            icon={<HelpOutlineIcon style={{ fontSize: 15 }} />}
            label="Unassigned"
            size="small"
            sx={{ bgcolor: "var(--fx-surface-muted, #F1F5F9)", color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 700, borderRadius: "6px" }}
          />
        );
    }
  };

  const normalizePhoneNumber = (value?: string) => {
    return String(value ?? "").replace(/\D/g, "");
  };

  const usageByPhoneNumber = new Map(usageItems.map((item) => [normalizePhoneNumber(item.phoneNumber), item]));

  const formatNumber = (value?: number) => {
    return new Intl.NumberFormat("en-US").format(Number(value ?? 0));
  };

  const formatMoney = (value?: number, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(Number(value ?? 0));
  };

  const UsageInline = ({ usage }: { usage?: IContactCenterUsageByNumber }) => {
    return (
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ whiteSpace: "nowrap" }}>
        <Typography variant="caption" color="var(--fx-surface-textsecondary, #64748B)" fontWeight={800}>
          Calls: {formatNumber(usage?.totalCallUnits)}
        </Typography>

        <Typography variant="caption" color="#CBD5E1">
          |
        </Typography>

        <Typography variant="caption" color="var(--fx-surface-textsecondary, #64748B)" fontWeight={800}>
          SMS: {formatNumber(usage?.totalSmsUnits)}
        </Typography>

        <Typography variant="caption" color="#CBD5E1">
          |
        </Typography>

        <Typography variant="caption" color="var(--fx-surface-textsecondary, #64748B)" fontWeight={700}>
          VM: {formatNumber(usage?.voicemailUnits)}
        </Typography>

        <Typography variant="caption" color="#CBD5E1">
          |
        </Typography>

        <Typography variant="caption" color="var(--fx-surface-textsecondary, #64748B)" fontWeight={700}>
          REC: {formatNumber(usage?.callRecordingUnits)}
        </Typography>
      </Stack>
    );
  };

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: "16px", border: "1px solid var(--fx-surface-border, #E2E8F0)" }}>
      {/* Header Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="var(--fx-surface-text, #0F172A)">
            Company Phone Numbers Directory
          </Typography>
          <Typography variant="body2" color="var(--fx-surface-textsecondary, #64748B)">
            All registered phone lines with usage for {billingPeriod}.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title="Refresh Numbers">
            <IconButton onClick={loadNumbers} size="small" sx={{ bgcolor: "var(--fx-surface-sunken, #F8FAFC)", border: "1px solid var(--fx-surface-border, #E2E8F0)" }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Filter and Sort Bar */}
      <Grid container spacing={2} alignItems="center" mb={2.5}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by phone, label, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)" }} />
                </InputAdornment>
              ),
              sx: { borderRadius: "10px", bgcolor: "var(--fx-surface-base, #FFFFFF)" },
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2.5}>
          <Select
            fullWidth
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ borderRadius: "10px", bgcolor: "var(--fx-surface-base, #FFFFFF)" }}
            startAdornment={
              <InputAdornment position="start">
                <FilterListIcon sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)", fontSize: 18 }} />
              </InputAdornment>
            }
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="unassigned">Unassigned</MenuItem>
          </Select>
        </Grid>

        {/* Sort Field Selector */}
        <Grid item xs={12} sm={4} md={3}>
          <Select
            fullWidth
            size="small"
            value={orderBy}
            onChange={(e) => setOrderBy(e.target.value as NumberOrderBy)}
            sx={{ borderRadius: "10px", bgcolor: "var(--fx-surface-base, #FFFFFF)" }}
            startAdornment={
              <InputAdornment position="start">
                <SortIcon sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)", fontSize: 18 }} />
              </InputAdornment>
            }
          >
            <MenuItem value="createdAt">Sort by: Created Date</MenuItem>
            <MenuItem value="phoneNumber">Sort by: Phone Number</MenuItem>
            <MenuItem value="companyName">Sort by: Company Name</MenuItem>
            <MenuItem value="status">Sort by: Status</MenuItem>
          </Select>
        </Grid>

        {/* Order Direction Toggle */}
        <Grid item xs={12} sm={2} md={2.5} display="flex" justifyContent="flex-end">
          <Tooltip title={`Order: ${order === "ASC" ? "Ascending" : "Descending"}`}>
            <IconButton
              onClick={toggleOrderDirection}
              sx={{
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                border: "1px solid var(--fx-surface-borderstrong, #CBD5E1)",
                borderRadius: "10px",
                px: 2,
                gap: 1,
              }}
            >
              <Typography variant="caption" fontWeight={700} color="var(--fx-surface-textsecondary, #64748B)">
                {order}
              </Typography>
              {order === "ASC" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>

      {/* Numbers Table */}
      <TableContainer sx={{ borderRadius: "12px", border: "1px solid var(--fx-surface-border, #E2E8F0)" }}>
        <Table sx={{ minWidth: 980 }}>
          <TableHead sx={{ bgcolor: "var(--fx-surface-sunken, #F8FAFC)" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)", width: 260 }}>Phone Line</TableCell>

              <TableCell sx={{ fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)", width: 220 }}>Company</TableCell>

              <TableCell sx={{ fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)" }} align="center">
                Provider
              </TableCell>

              <TableCell sx={{ fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)" }} align="center">
                Status
              </TableCell>

              <TableCell sx={{ fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)", minWidth: 250 }} align="center">
                Monthly Usage
              </TableCell>

              <TableCell sx={{ fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)", minWidth: 150 }} align="right">
                Monthly Amount
              </TableCell>

              <TableCell sx={{ fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)", minWidth: 150 }} align="center">
                Created
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="var(--fx-surface-textsecondary, #64748B)" mt={1}>
                    Loading phone numbers directory...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredNumbers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="var(--fx-surface-textsecondary, #64748B)">
                    No phone numbers found matching the selected filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredNumbers.map((item) => {
                const usage = usageByPhoneNumber.get(normalizePhoneNumber(item.phoneNumber));

                return (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{
                      "& td": {
                        py: 1.15,
                      },
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.25}>
                        <Avatar sx={{ bgcolor: "var(--fx-colors-info-50, #EFF6FF)", color: "#2563EB", width: 30, height: 30 }}>
                          <PhoneIcon fontSize="small" />
                        </Avatar>

                        <Box>
                          <Typography variant="subtitle2" fontWeight={700} color="var(--fx-surface-text, #0F172A)">
                            {item.phoneNumber}
                          </Typography>

                          <Typography variant="caption" color="var(--fx-surface-textsecondary, #64748B)">
                            {item.friendlyName}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <BusinessIcon sx={{ fontSize: 16, color: "var(--fx-surface-textsecondary, #64748B)" }} />

                        <Typography variant="body2" color="var(--fx-surface-textsecondary, #64748B)" fontWeight={500}>
                          {getCompanyName(item.companyId)}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={item.provider ? item.provider.replace("_", " ").toUpperCase() : "TWILIO"}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                          color: "var(--fx-surface-textsecondary, #64748B)",
                          bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                        }}
                      />
                    </TableCell>

                    <TableCell>{getStatusChip(item.status)}</TableCell>

                    <TableCell>
                      <UsageInline usage={usage} />

                      <Typography
                        variant="caption"
                        color="var(--fx-surface-texttertiary, #94A3B8)"
                        sx={{
                          display: "block",
                          mt: 0.35,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatNumber(usage?.callInboundUnits)} call in / {formatNumber(usage?.callOutboundUnits)} call out ·{" "}
                        {formatNumber(usage?.smsInboundUnits)} sms in / {formatNumber(usage?.smsOutboundUnits)} sms out
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={900} color="var(--fx-surface-text, #0F172A)">
                        {formatMoney(usage?.grandTotalAmount, usageCurrency)}
                      </Typography>

                      <Typography variant="caption" color="var(--fx-surface-texttertiary, #94A3B8)">
                        This period
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" color="var(--fx-surface-textsecondary, #64748B)">
                        {formatDate(item.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default NumbersListSubcomponent;
